import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axios, useAuth } from '../../context/AuthContext';
import { hasCoreManagementAccess, getCoreManagementBasePath } from '../../config/accessControl';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import toast from 'react-hot-toast';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ color: '#94A3B8', fontSize: '0.72rem', marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontSize: '0.85rem', fontWeight: 600 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed ? p.value.toFixed(1) : p.value : p.value}
        </div>
      ))}
    </div>
  );
};

export default function OgrenciDetayPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ogrenci, setOgrenci] = useState(null);
  const [analiz, setAnaliz] = useState(null);
  const [sonuclar, setSonuclar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aktifTab, setAktifTab] = useState('genel');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const canEditStudent = hasCoreManagementAccess(user?.rol) || user?.rol === 'ogretmen';

  useEffect(() => {
    const load = async () => {
      try {
        const detayRes = await axios.get(`/ogrenciler/${id}`);
        if (detayRes.data.success) {
          setOgrenci(detayRes.data.data);
          const d = detayRes.data.data;
          setEditForm({
            sinav_turu: d.sinav_turu || 'TYT',
            hedef_universite: d.hedef_universite || '',
            hedef_bolum: d.hedef_bolum || '',
            hedef_net: d.hedef_net || 0,
            hedef_puan: d.hedef_puan || 0,
            notlar: d.notlar || '',
          });
        }

        const [analizResult, sonucResult] = await Promise.allSettled([
          axios.get(`/ogrenciler/${id}/basari-analiz`),
          axios.get(`/denemeler/ogrenci/${id}`),
        ]);
        const analizRes = analizResult.status === 'fulfilled' ? analizResult.value : null;
        const sonucRes = sonucResult.status === 'fulfilled' ? sonucResult.value : null;
        if (analizRes?.data.success) setAnaliz(analizRes.data.data);
        if (sonucRes?.data.success) setSonuclar(sonucRes.data.data.sonuclar || []);
      } catch (e) { toast.error('Yükleme hatası'); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  const handleSave = async () => {
    if (!canEditStudent) return toast.error('Bu işlem için yetkiniz bulunmuyor.');
    try {
      await axios.put(`/ogrenciler/${id}`, editForm);
      toast.success('Güncellendi!');
      setEditMode(false);
    } catch { toast.error('Güncelleme hatası'); }
  };

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;
  if (!ogrenci) return <div className="empty-state"><h3>Öğrenci bulunamadı</h3></div>;

  const info = ogrenci;
  const sonNet = analiz?.grafik_data?.length ? parseFloat(analiz.grafik_data[analiz.grafik_data.length - 1]?.toplam_net || 0) : 0;
  const hedefNet = parseFloat(info.hedef_net || 0);
  const hedefPct = hedefNet > 0 ? Math.min((sonNet / hedefNet) * 100, 100) : 0;
  const trendColor = analiz?.trend === 'yukselen' ? '#10B981' : analiz?.trend === 'dusen' ? '#EF4444' : '#94A3B8';
  const trendIcon = analiz?.trend === 'yukselen' ? '↑' : analiz?.trend === 'dusen' ? '↓' : '→';

  const chartData = analiz?.grafik_data?.map((d, i) => ({
    name: `D${i + 1}`,
    net: parseFloat(d.toplam_net || 0),
    puan: parseFloat(d.puan || 0),
    hedef: hedefNet,
  })) || [];

  const dersData = analiz?.ders_netleri?.map(d => ({
    name: d.ders_adi,
    ort: parseFloat(d.ortalama_net || 0),
    max: parseFloat(d.max_net || 0),
    renk: d.renk,
  })) || [];

  const TABS = [
    { k: 'genel', l: '📊 Genel Bakış' },
    { k: 'denemeler', l: '📝 Denemeler' },
    { k: 'ders', l: '📚 Ders Analizi' },
    ...(canEditStudent ? [{ k: 'duzenle', l: '✏️ Düzenle' }] : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Back + Header */}
      <div className="animate-fadeInUp" style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`${getCoreManagementBasePath(user?.rol)}/ogrenciler`)}>← Geri</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="avatar avatar-lg" style={{ background: 'rgba(6,182,212,0.15)', color: '#06B6D4' }}>
              {ogrenci.ad?.[0]}{ogrenci.soyad?.[0]}
            </div>
            <div>
              <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.4rem' }}>{ogrenci.ad} {ogrenci.soyad}</h2>
              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <span className="badge badge-muted">{ogrenci.ogrenci_no}</span>
                <span className="badge badge-info">{info.sinav_turu}</span>
                <span className="badge badge-accent">{ogrenci.sinif_adi || 'Sınıf yok'}</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Veli: {ogrenci.veli_adi || '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Özet Metrikler */}
      <div className="grid-4 animate-fadeInUp stagger-1">
        {[
          { label: 'Son Net', value: parseFloat(sonNet||0).toFixed(1), color: 'var(--accent)', icon: '📊' },
          { label: 'Hedef Net', value: hedefNet, color: '#06B6D4', icon: '🎯' },
          { label: 'Trend', value: `${trendIcon} ${analiz?.trend || '—'}`, color: trendColor, icon: '📈' },
          { label: 'Rozetler', value: ogrenci.rozetler?.length || 0, color: '#8B5CF6', icon: '🏆' },
        ].map((m, i) => (
          <div key={i} className="stat-card">
            <div style={{ position: 'absolute', top: 0, right: 0, width: 60, height: 60, borderRadius: '0 var(--r-lg) 0 100%', background: m.color, opacity: 0.08 }} />
            <div style={{ fontSize: '1.3rem', marginBottom: 8 }}>{m.icon}</div>
            <div className="stat-number" style={{ color: m.color, fontSize: '1.6rem' }}>{m.value}</div>
            <div className="stat-label">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Hedef barı */}
      <div className="card animate-fadeInUp stagger-2">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700 }}>🎯 Hedefe İlerleme</span>
          <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, color: 'var(--accent)' }}>{hedefPct.toFixed(0)}%</span>
        </div>
        <div className="progress-bar" style={{ height: 10 }}>
          <div className="progress-fill" style={{ width: `${hedefPct}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Mevcut: {parseFloat(sonNet||0).toFixed(1)} net</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {hedefNet - sonNet > 0 ? `${(hedefNet - sonNet).toFixed(1)} net kaldı` : '✅ Hedefe ulaşıldı!'}
          </span>
        </div>
        {info.hedef_universite && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--accent-dim)', borderRadius: 8 }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--accent)' }}>
              🎓 Hedef: {info.hedef_universite} — {info.hedef_bolum}
            </span>
          </div>
        )}
      </div>

      {/* Devamsızlık & Ödev */}
      {(ogrenci.devamsizlik || ogrenci.odev_istatistik) && (
        <div className="grid-2 animate-fadeInUp stagger-2">
          <div className="card">
            <div style={{ fontFamily:'var(--font-ui)', fontWeight:800, marginBottom:14 }}>📋 Devamsızlık Durumu</div>
            {ogrenci.devamsizlik && (() => {
              const dev = ogrenci.devamsizlik;
              const pct = dev.toplam_ders > 0 ? Math.round((dev.devamsiz/dev.toplam_ders)*100) : 0;
              return (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>Toplam ders: <b style={{ color:'var(--text-primary)' }}>{dev.toplam_ders}</b></span>
                    <span style={{ fontSize:'0.82rem', fontWeight:700, color: pct > 20 ? '#EF4444' : '#10B981' }}>%{pct} devamsızlık</span>
                  </div>
                  <div style={{ display:'flex', gap:10, marginBottom:12 }}>
                    <div style={{ flex:1, padding:'10px', background:'#D1FAE5', borderRadius:10, textAlign:'center' }}>
                      <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.4rem', color:'#065F46' }}>{dev.mevcut}</div>
                      <div style={{ fontSize:'0.72rem', color:'#10B981', fontWeight:600 }}>Geldi</div>
                    </div>
                    <div style={{ flex:1, padding:'10px', background:'#FEE2E2', borderRadius:10, textAlign:'center' }}>
                      <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.4rem', color:'#991B1B' }}>{dev.devamsiz}</div>
                      <div style={{ fontSize:'0.72rem', color:'#EF4444', fontWeight:600 }}>Gelmedi</div>
                    </div>
                  </div>
                  <div style={{ height:8, background:'var(--bg-elevated)', borderRadius:99, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${100-pct}%`, background:'#10B981', borderRadius:99 }} />
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="card">
            <div style={{ fontFamily:'var(--font-ui)', fontWeight:800, marginBottom:14 }}>📝 Ödev Durumu</div>
            {ogrenci.odev_istatistik && (() => {
              const od = ogrenci.odev_istatistik;
              const pct = od.toplam > 0 ? Math.round((od.teslim_edildi/od.toplam)*100) : 0;
              return (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>Toplam ödev: <b style={{ color:'var(--text-primary)' }}>{od.toplam}</b></span>
                    <span style={{ fontSize:'0.82rem', fontWeight:700, color: pct < 60 ? '#EF4444' : '#10B981' }}>%{pct} teslim</span>
                  </div>
                  <div style={{ display:'flex', gap:10, marginBottom:12 }}>
                    <div style={{ flex:1, padding:'10px', background:'#D1FAE5', borderRadius:10, textAlign:'center' }}>
                      <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.4rem', color:'#065F46' }}>{od.teslim_edildi}</div>
                      <div style={{ fontSize:'0.72rem', color:'#10B981', fontWeight:600 }}>Yaptı</div>
                    </div>
                    <div style={{ flex:1, padding:'10px', background:'#FEE2E2', borderRadius:10, textAlign:'center' }}>
                      <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.4rem', color:'#991B1B' }}>{od.teslim_edilmedi}</div>
                      <div style={{ fontSize:'0.72rem', color:'#EF4444', fontWeight:600 }}>Yapmadı</div>
                    </div>
                  </div>
                  <div style={{ height:8, background:'var(--bg-elevated)', borderRadius:99, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:'#10B981', borderRadius:99 }} />
                  </div>
                  {pct < 60 && <div style={{ marginTop:8, padding:'6px 10px', background:'#FEF3C7', borderRadius:8, fontSize:'0.75rem', color:'#92400E' }}>⚠️ Ödev teslim oranı düşük!</div>}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tab-bar animate-fadeInUp stagger-2">
        {TABS.map(t => (
          <button key={t.k} className={`tab-btn ${aktifTab === t.k ? 'active' : ''}`} onClick={() => setAktifTab(t.k)}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Tab İçerikleri */}
      {aktifTab === 'genel' && (
        <div className="grid-2 animate-fadeIn">
          <div className="card">
            <div className="section-title" style={{ marginBottom: 16 }}>📈 Net Trendi</div>
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="ogrDetayGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="name" tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={hedefNet} stroke="var(--accent)" strokeDasharray="4 4" label={{ value: 'Hedef', fill: 'var(--accent)', fontSize: 10 }} />
                  <Area type="monotone" dataKey="net" stroke="#06B6D4" strokeWidth={2.5} fill="url(#ogrDetayGrad)" dot={{ fill: '#06B6D4', r: 3 }} name="Net" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>Yeterli deneme yok</p></div>}
          </div>
          <div className="card">
            <div className="section-title" style={{ marginBottom: 16 }}>📋 Kişisel Bilgiler</div>
            {[
              { l: 'E-posta', v: ogrenci.email },
              { l: 'Telefon', v: ogrenci.telefon || '—' },
              { l: 'Veli', v: ogrenci.veli_adi || '—' },
              { l: 'Veli Tel', v: ogrenci.veli_telefon || '—' },
              { l: 'Doğum', v: info.dogum_tarihi ? new Date(info.dogum_tarihi).toLocaleDateString('tr-TR') : '—' },
              { l: 'Adres', v: info.adres || '—' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 5 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item.l}</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{item.v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {aktifTab === 'denemeler' && (
        <div className="card animate-fadeIn">
          <div className="section-title" style={{ marginBottom: 16 }}>📝 Deneme Sonuçları</div>
          <div className="table-container">
            <table className="table">
              <thead><tr><th>Deneme</th><th>Tarih</th><th>Toplam Net</th><th>Puan</th><th>Hedefe Fark</th><th>Durum</th></tr></thead>
              <tbody>
                {sonuclar.length === 0
                  ? <tr><td colSpan={6}><div className="empty-state"><p>Deneme kaydı yok</p></div></td></tr>
                  : [...sonuclar].reverse().map((s, i) => {
                    const net = parseFloat(s.toplam_net || 0);
                    const fark = net - hedefNet;
                    const onceki = i < sonuclar.length - 1 ? parseFloat(sonuclar[sonuclar.length - 2 - i]?.toplam_net || 0) : null;
                    const artis = onceki !== null ? net - onceki : 0;
                    return (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{s.deneme_adi}</td>
                        <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{s.tarih ? new Date(s.tarih).toLocaleDateString('tr-TR') : '—'}</td>
                        <td><span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: 'var(--accent)', fontSize: '1.05rem' }}>{parseFloat(net||0).toFixed(1)}</span></td>
                        <td style={{ color: '#3B82F6', fontWeight: 600 }}>{parseFloat(s.puan || 0).toFixed(0)}</td>
                        <td><span style={{ color: fark >= 0 ? '#10B981' : '#EF4444', fontWeight: 600 }}>{fark >= 0 ? '+' : ''}{parseFloat(fark||0).toFixed(1)}</span></td>
                        <td>
                          {i === 0 ? <span className="badge badge-muted">Son</span>
                            : artis > 0 ? <span className="badge badge-success">↑ +{parseFloat(artis||0).toFixed(1)}</span>
                            : artis < 0 ? <span className="badge badge-danger">↓ {parseFloat(artis||0).toFixed(1)}</span>
                            : <span className="badge badge-muted">→</span>}
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {aktifTab === 'ders' && (
        <div className="card animate-fadeIn">
          <div className="section-title" style={{ marginBottom: 16 }}>📚 Ders Bazlı Net Ortalamaları</div>
          {dersData.length === 0
            ? <div className="empty-state"><p>Yeterli veri yok</p></div>
            : <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dersData} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="name" tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#4B5563', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="ort" name="Ort. Net" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="max" name="En Yüksek" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                {dersData.sort((a, b) => a.ort - b.ort).slice(0, 3).map((d, i) => (
                  <div key={i} style={{
                    padding: '10px 14px',
                    background: 'var(--danger-dim)',
                    borderRadius: 10,
                    border: '1px solid rgba(239,68,68,0.15)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ color: '#EF4444', fontWeight: 600, fontSize: '0.85rem' }}>
                      ⚠️ {d.name} — Zayıf alan
                    </span>
                    <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: '#EF4444' }}>{d.parseFloat(ort||0).toFixed(1)} net</span>
                  </div>
                ))}
              </div>
            </>
          }
        </div>
      )}

      {aktifTab === 'duzenle' && canEditStudent && (
        <div className="card animate-fadeIn" style={{ maxWidth: 640 }}>
          <div className="section-title" style={{ marginBottom: 20 }}>✏️ Öğrenci Bilgilerini Düzenle</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              { k: 'hedef_universite', l: 'Hedef Üniversite', t: 'text' },
              { k: 'hedef_bolum', l: 'Hedef Bölüm', t: 'text' },
              { k: 'hedef_net', l: 'Hedef Net', t: 'number' },
              { k: 'hedef_puan', l: 'Hedef Puan', t: 'number' },
            ].map(f => (
              <div className="form-group" key={f.k}>
                <label className="form-label">{f.l}</label>
                <input className="form-input" type={f.t} value={editForm[f.k] || ''}
                  onChange={e => setEditForm({ ...editForm, [f.k]: e.target.value })} />
              </div>
            ))}
            <div className="form-group">
              <label className="form-label">Sınav Türü</label>
              <select className="form-select" value={editForm.sinav_turu}
                onChange={e => setEditForm({ ...editForm, sinav_turu: e.target.value })}>
                {['TYT','AYT-SAY','AYT-SOZ','AYT-EA','AYT-DIL','LGS'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Notlar</label>
              <textarea className="form-textarea" value={editForm.notlar || ''}
                onChange={e => setEditForm({ ...editForm, notlar: e.target.value })} placeholder="Öğrenci hakkında notlar..." />
            </div>
          </div>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={handleSave}>
            💾 Kaydet
          </button>
        </div>
      )}
    </div>
  );
}
